import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useFetchWithMsal from '../useFetchWithMsal';
import { protectedResources } from '../../authConfig';
import { useToast } from '../../contexts/ToastContext';

const useAccountingFirms = () => {
  const { showToast } = useToast(); 
  const [editingFirm, setEditingFirm] = useState(null);  
  const [newFirm, setNewFirm] = useState({ 
    companyName: "", 
    companyId: "", 
    companyNumber: "",
    vatFileNumber: "",
    accountants: [
      {
        name: "",
        accountant: "",
        email: "",
        receiveEmailNotifications: true,
        isPrimary: true
      }
    ]
  });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [firmToDelete, setFirmToDelete] = useState(null);
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  const queryClient = useQueryClient();

  const handleAccountantChange = (value) => {
    setNewFirm(prev => ({ ...prev, accountant: value }));
  };

  const handleEditAccountantChange = (value) => {
    setEditingFirm(prev => ({ ...prev, accountant: value }));
  };

  // Functions for managing accountants list in new firm
  const addAccountantToNewFirm = () => {
    setNewFirm(prev => ({
      ...prev,
      accountants: [
        ...prev.accountants,
        {
          name: "",
          accountant: "",
          email: "",
          receiveEmailNotifications: true,
          isPrimary: false
        }
      ]
    }));
  };

  const removeAccountantFromNewFirm = (index) => {
    setNewFirm(prev => {
      const newAccountants = prev.accountants.filter((_, i) => i !== index);
      // If we removed the primary, make the first one primary
      if (newAccountants.length > 0 && !newAccountants.some(acc => acc.isPrimary)) {
        newAccountants[0].isPrimary = true;
      }
      return { ...prev, accountants: newAccountants };
    });
  };

  const updateAccountantInNewFirm = (index, field, value) => {
    setNewFirm(prev => {
      const newAccountants = [...prev.accountants];
      newAccountants[index] = { ...newAccountants[index], [field]: value };
      
      // If setting this one as primary, unset others
      if (field === 'isPrimary' && value === true) {
        newAccountants.forEach((acc, i) => {
          if (i !== index) acc.isPrimary = false;
        });
      }
      
      return { ...prev, accountants: newAccountants };
    });
  };

  // Functions for managing accountants list in editing firm
  const addAccountantToEditFirm = () => {
    setEditingFirm(prev => ({
      ...prev,
      accountants: [
        ...prev.accountants,
        {
          name: "",
          accountant: "",
          email: "",
          receiveEmailNotifications: true,
          isPrimary: false
        }
      ]
    }));
  };

  const removeAccountantFromEditFirm = (index) => {
    setEditingFirm(prev => {
      const newAccountants = prev.accountants.filter((_, i) => i !== index);
      // If we removed the primary, make the first one primary
      if (newAccountants.length > 0 && !newAccountants.some(acc => acc.isPrimary)) {
        newAccountants[0].isPrimary = true;
      }
      return { ...prev, accountants: newAccountants };
    });
  };

  const updateAccountantInEditFirm = (index, field, value) => {
    setEditingFirm(prev => {
      const newAccountants = [...prev.accountants];
      newAccountants[index] = { ...newAccountants[index], [field]: value };
      
      // If setting this one as primary, unset others
      if (field === 'isPrimary' && value === true) {
        newAccountants.forEach((acc, i) => {
          if (i !== index) acc.isPrimary = false;
        });
      }
      
      return { ...prev, accountants: newAccountants };
    });
  };

  const fetchAccountingFirms = async () => {
    try {
      const response = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/accounting-firms`
      );

      if (!response) throw new Error('Failed to fetch accounting firms');
      return response;
    } catch (error) {
      console.error('Error fetching accounting firms:', error);
      throw error;
    }
  };

  const {
    data: firms = { accountingFirms: [] },
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['accountingFirms', forceRefresh],
    queryFn: fetchAccountingFirms,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    staleTime: 0
  });

  const addFirmMutation = useMutation({
    mutationFn: async (newFirmData) => {
      const response = await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/accounting-firms`,
        newFirmData
      );
      if (!response) throw new Error('Failed to add firm');
      return response;
    },
    onSuccess: () => {
      showToast("החברה נוספה בהצלחה", "success"); 
      resetNewFirmForm(); 
      setIsAddModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['accountingFirms'] });
    },
    onError: (error) => {
      console.error('Error adding firm:', error);
      showToast('שגיאה בהוספת החברה', "error"); 
    }
  });

  const updateFirmMutation = useMutation({
    mutationFn: async (firm) => {
      // Clean the firm data before sending
      const cleanFirm = {
        ...firm,
        accountants: firm.accountants?.map(acc => {
          const cleanAcc = { ...acc };
          // Remove temporary _id if it exists
          if (cleanAcc._id && typeof cleanAcc._id === 'string' && cleanAcc._id.length < 24) {
            delete cleanAcc._id;
          }
          return cleanAcc;
        })
      };
      
      const response = await execute(
        "PUT",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/accounting-firms/${firm._id}`,
        cleanFirm
      );
      if (!response) throw new Error('Failed to update firm');
      return response;
    },
    onSuccess: (data, variables) => {
      // Don't show toast if this is a silent update (primary change from table)
      const isSilentUpdate = variables._silentUpdate;
      
      if (!isSilentUpdate) {
        showToast("החברה עודכנה בהצלחה", "success");
        setIsEditModalOpen(false);
      }
      
      queryClient.invalidateQueries({ queryKey: ['accountingFirms'] });
    },
    onError: (error) => {
      console.error('Error updating firm:', error);
      showToast('שגיאה בעדכון חברה', "error"); 
    }
  });

  const deleteFirmMutation = useMutation({
    mutationFn: async (id) => {
      const response = await execute(
        "DELETE",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/accounting-firms/${id}`
      );
      if (!response) throw new Error('Failed to delete firm');
      return response;
    },
    onSuccess: () => {
      showToast("החברה נמחקה בהצלחה", "success");
      queryClient.invalidateQueries({ queryKey: ['accountingFirms'] });
      setIsDeleteDialogOpen(false); 
    },
    onError: (error) => {
      console.error('Error deleting firm:', error);
      showToast('שגיאה במחיקת חברה', "error");
    }
  });

  const refreshData = () => {
    queryClient.invalidateQueries(['accountingFirms']);
    setForceRefresh(prev => prev + 1);
  };

  const resetNewFirmForm = () => {
    setNewFirm({ 
      companyName: "", 
      companyId: "", 
      companyNumber: "", 
      vatFileNumber: "",
      accountants: [
        {
          name: "",
          accountant: "",
          email: "",
          receiveEmailNotifications: true,
          isPrimary: true
        }
      ]
    });
  };

  const handleAdd = () => {
    if (!newFirm.companyName.trim()) {
      showToast("שם החברה הוא שדה חובה", "error", { inline: true });
      return;
    }

    if (!newFirm.accountants || newFirm.accountants.length === 0) {
      showToast("חובה להוסיף לפחות מנהלת חשבונות אחת", "error", { inline: true });
      return;
    }

    // Validate all accountants
    for (let i = 0; i < newFirm.accountants.length; i++) {
      const acc = newFirm.accountants[i];
      if (!acc.accountant.trim()) {
        showToast(`שם המשתמש הוא שדה חובה במנהלת חשבונות ${i + 1}`, "error", { inline: true });
        return;
      }
      if (!acc.email.trim()) {
        showToast(`כתובת המייל היא שדה חובה במנהלת חשבונות ${i + 1}`, "error", { inline: true });
        return;
      }
    }

    addFirmMutation.mutate(newFirm);
  };

  const handleUpdate = () => {
    if (!editingFirm.companyName.trim()) {
      showToast("שם החברה הוא שדה חובה", "error", { inline: true });
      return;
    }
    
    if (!editingFirm.accountants || editingFirm.accountants.length === 0) {
      showToast("חובה להוסיף לפחות מנהלת חשבונות אחת", "error", { inline: true });
      return;
    }

    // Validate all accountants
    for (let i = 0; i < editingFirm.accountants.length; i++) {
      const acc = editingFirm.accountants[i];
      if (!acc.accountant.trim()) {
        showToast(`שם המשתמש הוא שדה חובה במנהלת חשבונות ${i + 1}`, "error", { inline: true });
        return;
      }
      if (!acc.email.trim()) {
        showToast(`כתובת המייל היא שדה חובה במנהלת חשבונות ${i + 1}`, "error", { inline: true });
        return;
      }
    }
    
    updateFirmMutation.mutate(editingFirm);
  };

  const handleDeleteClick = (id, name) => {
    setFirmToDelete({ _id: id, companyName: name });
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (firmToDelete) {
      deleteFirmMutation.mutate(firmToDelete._id);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setFirmToDelete(null);
  };

  const handleEdit = (firm) => {
    // Convert old format to new format if needed
    let firmToEdit = { ...firm };
    
    if (!firmToEdit.accountants || firmToEdit.accountants.length === 0) {
      // Old format - convert to new
      firmToEdit.accountants = [{
        name: firmToEdit.name || '',
        accountant: firmToEdit.accountant || '',
        email: firmToEdit.email || '',
        receiveEmailNotifications: firmToEdit.receiveEmailNotifications !== false,
        isPrimary: true
      }];
    }
    
    setEditingFirm(firmToEdit);
    setIsEditModalOpen(true);
  };

  useEffect(() => {
    refreshData();
  }, []);

  return {
    firms,
    isLoading,
    isLoadingEmail,
    error,
    newFirm,
    setNewFirm,
    editingFirm,
    setEditingFirm,
    isAddModalOpen,
    setIsAddModalOpen,
    isEditModalOpen,
    setIsEditModalOpen,
    handleAdd,
    handleUpdate,
    handleDeleteClick,
    handleCancelDelete,
    handleConfirmDelete,
    isDeleteDialogOpen,
    firmToDelete,
    isDeleting: deleteFirmMutation.isPending,
    handleEdit,
    refreshData,
    handleAccountantChange,
    handleEditAccountantChange,
    addAccountantToNewFirm,
    removeAccountantFromNewFirm,
    updateAccountantInNewFirm,
    addAccountantToEditFirm,
    removeAccountantFromEditFirm,
    updateAccountantInEditFirm,
    updateFirmMutation
  };
};

export default useAccountingFirms;